import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Mail, Check, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UnsubscribePage = () => {
  const [params] = useSearchParams();
  const emailFromLink = params.get("email") || "";
  const [email, setEmail] = useState(emailFromLink);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleUnsubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/newsletter/unsubscribe`, { email });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Couldn't unsubscribe right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-[#0A0A0A]">
      <div className="max-w-md mx-auto">
        <div
          className="bg-[#121212] border border-white/10 p-8 text-[#F4F1DE]"
          data-testid="unsubscribe-page"
        >
          {!done ? (
            <>
              <div className="w-12 h-12 rounded-lg bg-[#E07A5F]/15 border border-[#E07A5F]/30 flex items-center justify-center mb-5">
                <Mail className="w-6 h-6 text-[#E07A5F]" />
              </div>
              <h1 className="text-2xl font-bold font-['Space_Grotesk'] leading-tight mb-2">
                Unsubscribe from ReptiCraft
              </h1>
              <p className="text-sm text-[#F4F1DE]/60 mb-5 leading-relaxed">
                We're sorry to see you go. Confirm your email below and we'll remove you from all marketing emails immediately.
              </p>

              <form onSubmit={handleUnsubscribe} className="space-y-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded px-3 py-2.5 text-sm text-[#F4F1DE] placeholder-[#F4F1DE]/30 focus:border-[#E07A5F] focus:outline-none"
                  disabled={loading}
                  required
                  data-testid="unsubscribe-email-input"
                />
                {error && (
                  <p className="text-xs text-red-400" data-testid="unsubscribe-error">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold"
                  data-testid="unsubscribe-submit-btn"
                >
                  {loading ? "Processing..." : "Confirm unsubscribe"}
                </Button>
              </form>

              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-1 text-xs text-[#F4F1DE]/40 hover:text-[#F4F1DE]/70 transition-colors"
              >
                <ArrowLeft className="w-3 h-3" />
                Back to ReptiCraft
              </Link>

              <p className="text-[10px] text-[#F4F1DE]/30 mt-5 leading-relaxed">
                This won't affect any active orders — only marketing emails.
              </p>
            </>
          ) : (
            <div data-testid="unsubscribe-success">
              <div className="w-12 h-12 rounded-lg bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold font-['Space_Grotesk'] mb-2">
                You've been unsubscribed
              </h1>
              <p className="text-sm text-[#F4F1DE]/60 mb-5">
                <span className="text-[#F4F1DE]/80">{email}</span> has been removed from the ReptiCraft marketing list.
                You won't receive any more promotional emails.
              </p>
              <p className="text-xs text-[#F4F1DE]/40 mb-6">
                Transactional emails (order confirmations, shipping updates) will still be sent for any orders you place.
              </p>
              <Link to="/">
                <Button className="w-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-semibold">
                  Back to ReptiCraft
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
